import { useNavigate } from '@tanstack/react-router'
import { SelectedTab } from 'features/navbar/types'
import {
  type ComputedTrendRecord,
  saveComputedTrend,
} from 'features/timeSeries/computed/computedTrendStore'
import { useOptionalWorkspaceTabsActions } from 'features/workspaceTabs/contexts/WorkspaceTabsContext'
import { useCallback } from 'react'

export const computedTrendResourceKey = (key: string): string =>
  `computed-trend:${key}`

export const computedTrendHref = (record: ComputedTrendRecord): string => {
  const params = new URLSearchParams({ siteId: record.siteId })
  params.set('computedKey', record.key)
  if (record.contextEntryIds?.length) {
    params.set('sensorIds', record.contextEntryIds.join(','))
  }
  if (record.startDatetime) params.set('start', record.startDatetime)
  if (record.endDatetime) params.set('end', record.endDatetime)
  return `/process-data/view?${params.toString()}`
}

/**
 * Open an agent-computed trend on the Process Data surface: a resource tab in
 * the workspace layout, else a navigation to /process-data/view. Persisted to
 * session storage first so the route and a refresh can rehydrate it.
 */
export const useOpenComputedTrendResource = () => {
  const actions = useOptionalWorkspaceTabsActions()
  const navigate = useNavigate()

  return useCallback(
    (record: ComputedTrendRecord) => {
      saveComputedTrend(record)
      if (actions) {
        actions.openResourceTab({
          resourceKey: computedTrendResourceKey(record.key),
          workspaceDestination: SelectedTab.ProcessData,
          label: record.label,
          href: computedTrendHref(record),
        })
        return
      }
      void navigate({
        to: '/process-data/view',
        search: {
          siteId: record.siteId,
          computedKey: record.key,
          sensorIds: record.contextEntryIds?.join(','),
          start: record.startDatetime,
          end: record.endDatetime,
        },
      })
    },
    [actions, navigate]
  )
}
